import React from 'react'
import { Link } from 'react-router-dom'
import { useQueryParams, NumberParam, withDefault, JsonParam } from 'use-query-params'

import EqualsFilter from '@/gf/modules/filters/Equals'
import FilterSet from '@/gf/modules/FilterSet'
import FormatFilters from '@/gf/modules/FormatFilters'
import useProducts from '@/gf/hooks/useProducts'
import useSession from '../hooks/useSession'
import useSelection from '@/gf/hooks/useSelection'
import useToggle from '@/gf/hooks/useToggle'
import User from '@/gf/modules/User'

import Page from '@/gf/components/Page'
import ProductsTable from '@/gf/components/ProductsTable'
import ProductUpdates from '@/gf/components/ProductUpdates'
import UpdateEnabledModal from '@/gf/components/modals/UpdateEnabled'
import UpdateBrandsModal from '@/gf/components/modals/UpdateBrands'
import UpdateAvailabilityModal from '@/gf/components/modals/UpdateAvailability'
import UpdateShippingModal from '@/gf/components/modals/UpdateShipping'
import UpdateShippingDimensionsModal from '../components/modals/UpdateShippingDimensions'
import UpdateShippingWeightModal from '../components/modals/UpdateShippingWeight'
import UpdateShippingLtlModal from '../components/modals/UpdateShippingLtl'
import UpdateSalePriceModal from '@/gf/components/modals/UpdateSalePrice'
import SetCategoryModal from '@/gf/components/SetCategoryModal'
import Frame from '../components/Frame'

import { Filter } from '../../types'

const breadcrumbs = {
  copy: 'Back to Dashboard',
  crumbs: [{ name: 'Products', href: '/products' }],
}

const useQueryParamsOpts = {
  page: withDefault(NumberParam, 1),
  filters: withDefault<Filter[], Filter[]>(JsonParam, []),
}

const Products = () => {
  const { store, user } = useSession()

  const [updateSalePriceModalOpen, updateSalePriceModalToggler] = useToggle()
  const [disabledToggleOpen, disabledModalToggler] = useToggle()
  const [brandsToggleOpen, brandsModalToggler] = useToggle()
  const [enabledToggleOpen, enabledModalToggler] = useToggle()
  const [shippingToggleOpen, shippingModalToggler] = useToggle()
  const [ltlModalOpen, ltlModalToggler] = useToggle()
  const [weightModalOpen, weightModalToggler] = useToggle()
  const [dimensionsModalOpen, dimensionsModalToggler] = useToggle()
  const [availabilityToggleOpen, availabilityModalToggler] = useToggle()
  const [setCategoryModalOpen, setCategoryModalToggler] = useToggle()
  const [query, updateQuery] = useQueryParams(useQueryParamsOpts)

  const initFilters = User.isAdmin(user)
    ? [EqualsFilter.build({ fieldId: 'offline', text: 'false' })]
    : [
        EqualsFilter.build({ fieldId: 'offline', text: 'false' }),
        EqualsFilter.build({ fieldId: 'storeId', text: store.id }),
      ]

  const filters = [...query.filters, ...initFilters]

  const gqlFilters = FormatFilters.andFilters(FilterSet.toApiFilters(filters))

  const variables = {
    filters: gqlFilters,
    page: query.page,
  }

  const { data, refetch } = useProducts({ variables })

  const productsSearch = data?.productsSearch

  const selection = useSelection(productsSearch?.products.map((p) => p.id) || [])

  React.useEffect(() => {
    if (!selection.all) selection.deselectAll()
  }, [query])

  const updatePage = (newPage: number) => updateQuery({ page: newPage })
  const updateFilters = (newFilters: Filter[]) => updateQuery({ page: 1, filters: newFilters })

  const refresh = () => {
    refetch(variables)
  }

  const selectedCount =
    selection.all && productsSearch
      ? productsSearch?.pagination.totalResults
      : selection.selectedIds.length

  const selectAllLabel = `Select all ${productsSearch?.pagination.totalResults.toLocaleString()} products`

  const optionsChunk1 = [{ label: 'Update Sale Price', action: updateSalePriceModalToggler.on }]

  const optionsChunk2 =
    User.isAdmin(user) || !store.shipsFree
      ? [{ label: 'Update Shipping', action: shippingModalToggler.on }]
      : []

  const optionsChunk3 = [
    { label: 'Update Availability', action: availabilityModalToggler.on },
    { label: 'Set Shipping Weight', action: weightModalToggler.on },
    { label: 'Set Shipping Dimensions', action: dimensionsModalToggler.on },
    { label: 'Set Shipping Ltl', action: ltlModalToggler.on },
    { label: 'Edit Brands', action: brandsModalToggler.on },
    { label: 'Set to Enabled', action: enabledModalToggler.on },
    { label: 'Set to Disabled', action: disabledModalToggler.on },
    { label: 'Set Category', action: setCategoryModalToggler.on },
  ]

  return (
    <Frame breadcrumbs={breadcrumbs}>
      <Page
        title="Products"
        titleRightContent={
          <div className="flex gap-2">
            <Link
              to="/product-diagnostics"
              className="text-white border-transparent bg-gearflow hover:bg-yellow-700 focus:ring-yellow-500 inline-flex items-center px-4 py-2 border text-sm rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              Diagnostics
            </Link>
            <Link
              to="/products/create"
              className="text-white border-transparent bg-gearflow hover:bg-yellow-700 focus:ring-yellow-500 inline-flex items-center px-4 py-2 border text-sm rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              Create Product
            </Link>
          </div>
        }
      >
        <UpdateSalePriceModal
          isOpen={updateSalePriceModalOpen}
          close={updateSalePriceModalToggler.off}
          selection={selection}
          selectedCount={selectedCount}
          productFilters={filters}
          onComplete={refresh}
        />
        <UpdateShippingModal
          modalOpen={shippingToggleOpen}
          onClose={shippingModalToggler.off}
          selection={selection}
          productFilters={filters}
          selectedCount={selectedCount}
          onComplete={refresh}
        />
        <UpdateShippingWeightModal
          modalOpen={weightModalOpen}
          onClose={weightModalToggler.off}
          selection={selection}
          filters={gqlFilters}
          selectedCount={selectedCount}
          onComplete={refresh}
        />
        <UpdateShippingDimensionsModal
          modalOpen={dimensionsModalOpen}
          onClose={dimensionsModalToggler.off}
          selection={selection}
          filters={gqlFilters}
          selectedCount={selectedCount}
          onComplete={refresh}
        />
        <UpdateShippingLtlModal
          modalOpen={ltlModalOpen}
          onClose={ltlModalToggler.off}
          selection={selection}
          filters={gqlFilters}
          selectedCount={selectedCount}
          onComplete={refresh}
        />
        <UpdateAvailabilityModal
          modalOpen={availabilityToggleOpen}
          onClose={availabilityModalToggler.off}
          selection={selection}
          productFilters={filters}
          selectedCount={selectedCount}
          onComplete={refresh}
        />
        <UpdateBrandsModal
          modalOpen={brandsToggleOpen}
          onClose={brandsModalToggler.off}
          selection={selection}
          productFilters={filters}
          selectedCount={selectedCount}
          onComplete={refresh}
        />
        <UpdateEnabledModal
          modalOpen={disabledToggleOpen}
          onClose={disabledModalToggler.off}
          enabled={false}
          selection={selection}
          filters={gqlFilters}
          selectedCount={selectedCount}
          onComplete={refresh}
        />
        <UpdateEnabledModal
          modalOpen={enabledToggleOpen}
          onClose={enabledModalToggler.off}
          enabled
          selection={selection}
          filters={gqlFilters}
          selectedCount={selectedCount}
          onComplete={refresh}
        />
        <SetCategoryModal
          isOpen={setCategoryModalOpen}
          close={setCategoryModalToggler.off}
          selection={selection}
          selectedCount={selectedCount}
          productFilters={filters}
          onComplete={refresh}
        />
        <ProductsTable
          page={query.page}
          selection={selection}
          filters={query.filters}
          updatePage={updatePage}
          updateFilters={updateFilters}
          productsSearch={productsSearch}
        >
          <ProductUpdates
            selection={selection}
            options={[...optionsChunk1, ...optionsChunk2, ...optionsChunk3]}
            selectAllLabel={selectAllLabel}
          />
        </ProductsTable>
      </Page>
    </Frame>
  )
}

export default Products
